import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import { useWidth } from '../../lib/useWidth';
import {
  Button,
  BreadcrumbComponent,
  Title,
  Subtitle,
  DropdownMenu,
  BgIcon,
  ExpandArrowIcon,
  OwnerIcon,
  ClockIcon,
} from '../../components/shared';
import NameWithPercent from '../../components/shared/NameWithPercent';
import Tooltip from '../../components/shared/Tooltip';
import { sendSingleRequest } from '../../apis';
import { Image, Button as SmallButton } from 'antd';
import farm_image from '../../images/farm.png';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import { loadSpatStorages } from '../../store/farms/farms.actions';
import { selectLang } from '../../store/ui/ui.selector';
import LinesTable from '../../components/shared/tables/LinesTable';
import {
  amountDays,
  displayValueUnit,
  randomKey,
} from '../../lib/common.helpers';
import BulkHarvestModal from '../../components/farm-modals/BulkHarvestModal';
import LinesMobile from '../../components/shared/table-mobile/LinesMobile';
import { translate } from '../../lib/lang.helper';
import {
  selectAllFarms,
  SelectIsEditable,
} from '../../store/extra/extra.selector';
import InterestGrowth from '../../components/shared/tables-card/InterestGrowth';
import { ForecastCell } from '../../components/CommonViews';
import { selectAccount } from '../../store/auth/auth.selector';
import LinesBulkUpdateModal from '../../components/lines/LinesBulkUpdateModal';
import RaftColumnModifyModal from '../../components/user-settings/RaftColumnModifyModal';
import LineColumnModifyModal from '../../components/user-settings/LineColumnModifyModal';

interface IValues {
  value?: number;
  isGrow?: boolean;
  interest?: number;
  isReverse?: boolean;
}

const getInterest = (
  firstValue: number,
  secondValue: number | null,
): IValues => {
  let interest;
  let values: IValues = {};
  if (secondValue !== null && secondValue !== 0) {
    interest = ((firstValue - secondValue) / secondValue) * 100;
    values = {
      isGrow: !(secondValue > firstValue),
      interest: Number(Math.abs(interest).toFixed(2)),
      value: firstValue,
    };
  } else if (secondValue === 0) {
    values = {
      isGrow: !(+firstValue < 0),
      interest: +firstValue === 0 ? 0 : 100,
      value: firstValue,
    };
  } else if (secondValue === null) {
    values = {
      isGrow: true,
      interest: 0,
      value: firstValue,
    };
  } else {
    values = {
      value: firstValue,
    };
  }

  return values;
};

interface IApiFarmCard {
  name: string;
  this_year: number;
  last_year: number;
  interest: any;
}

const Farm = () => {
  const width = useWidth();
  const history = useHistory();
  const dispatch = useDispatch();

  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectAllFarms);
  const unitType = useSelector(selectAccount)?.display_unit;

  const params = useParams<{ idFarm: string }>();
  const currentFarm = farmsData.find(x => x.id === Number(params.idFarm));
  const isEditable = SelectIsEditable(Number(params.idFarm));

  const [stats, setStats] = useState<IApiFarmCard[]>([]);
  const [showBulkHarvest, setShowBulkHarvest] = useState<
    'MUSSEL' | 'OYSTER' | 'SEAWEED'
  >();
  const [visModify, setVisModify] = useState(false);
  const [visBulk, setVisBulk] = useState(false);

  useEffect(() => {
    dispatch(loadSpatStorages());
  }, [dispatch]);

  const breadcrumbItems = getBreadcrumbMenu('FARM_DETAIL', lang, {
    FARM_ID: params.idFarm,
    FARM_NAME: currentFarm?.name,
  });

  const loadStats = async () => {
    try {
      const response = await sendSingleRequest(
        { farm_id: params.idFarm },
        'GET',
        'api/overview/farm-budget-info',
        true,
      );
      if (response.status) {
        const newGeneralStats = response.data?.map((item: any) => {
          const interest = getInterest(
            item?.this_year,
            item?.last_year ? item?.last_year : null,
          );
          return { ...item, interest };
        });
        setStats(newGeneralStats);
      }
    } catch (e) {
      console.error('loadStats error:', e);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleOnEdit = () => {
    history.push(`/farms/farm-edit/${params.idFarm}`);
  };

  const tooltipItems =
    currentFarm?.owners?.map((x, i) => {
      return {
        title: `${x.title} - ${x.percent}%`,
        id: i + 1,
      };
    }) ?? [];

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative farms__line'>
        {width > 768 && (
          <div className='d-flex justify-between align-center pl-21 pr-15 pt-28 pb-28'>
            <BreadcrumbComponent items={breadcrumbItems} />
            <div className='d-flex align-center'>
              {currentFarm?.lines.some(x => x.crop_type === 'MUSSEL') && (
                <div className='mr-12'>
                  <Button
                    color='green'
                    size={2}
                    width='middle'
                    type='fill'
                    onClick={() => setShowBulkHarvest('MUSSEL')}
                  >
                    {translate(lang, 'Bulk Harvest Mussels')}
                  </Button>
                </div>
              )}
              {currentFarm?.lines.some(x => x.crop_type === 'OYSTER') && (
                <div className='mr-12'>
                  <Button
                    color='green'
                    size={2}
                    width='middle'
                    type='fill'
                    onClick={() => setShowBulkHarvest('OYSTER')}
                  >
                    {translate(lang, 'Bulk Harvest Oysters')}
                  </Button>
                </div>
              )}
              {!currentFarm?.is_flupsy && (
                <>
                  {isEditable && (
                    <Link to={`/farms/${params.idFarm}/add-line`}>
                      <Button color='blue' size={2} width='middle' type='fill'>
                        {translate(lang, 'Add Line')}
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className='farms__line-content'>
          <div className='d-flex justify-between white-card farms__line--header'>
            <div className='farms__line--header__name'>
              <div>
                <Title
                  size={5}
                  color='black-3'
                  align='default'
                  fontWeight={500}
                >
                  {currentFarm?.name}
                </Title>
                <Subtitle size={4} color='black' align='left' fontWeight={400}>
                  <span className='pr-6'>{currentFarm?.farm_number}</span>
                </Subtitle>
              </div>
              {width <= 768 && isEditable && (
                <div className='d-flex align-center'>
                  <DropdownMenu
                    data={currentFarm}
                    column='isFarms'
                    onEdit={handleOnEdit}
                    type='isRedirect'
                    isRedirect='isFarms'
                  />
                </div>
              )}
            </div>
            <div className='d-flex align-center farms__line--header__item'>
              <BgIcon color='orange' icon={<ExpandArrowIcon />} />
              <div className='pl-12'>
                <Subtitle size={3} color='black' align='left' fontWeight={400}>
                  {translate(lang, 'Area')}
                </Subtitle>
                <Subtitle size={4} color='black' align='left' fontWeight={500}>
                  {displayValueUnit(
                    currentFarm?.area,
                    unitType,
                    'area_hectare',
                  )}
                </Subtitle>
              </div>
            </div>
            <div className='d-flex align-center farms__line--header__item'>
              <BgIcon color='blue' icon={<OwnerIcon />} />
              <div className='pl-12'>
                <Subtitle
                  size={3}
                  color='black'
                  align='left'
                  fontWeight={400}
                  className='pb-4'
                >
                  {translate(lang, 'Owner')}
                </Subtitle>
                <Tooltip content={tooltipItems} position='bottom'>
                  <div className='tooltip d-flex'>
                    {currentFarm?.owners?.map((x, i) => {
                      const key = randomKey();
                      return (
                        <div key={key} className='table__owner'>
                          <NameWithPercent
                            title={x.title}
                            percent={x.percent}
                            index={i + 1}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className='d-flex align-center farms__line--header__item'>
              <BgIcon color='green' icon={<ClockIcon />} />
              <div className='pl-12'>
                <Subtitle size={3} color='black' align='left' fontWeight={400}>
                  {translate(lang, '_average_line_empty_per')}
                </Subtitle>
                <Subtitle size={4} color='black' align='left' fontWeight={500}>
                  {amountDays(currentFarm?.idle_avg ?? 0, lang)}
                </Subtitle>
              </div>
            </div>
            <div className='d-flex align-center farms__line--header__item'>
              {currentFarm && <ForecastCell farm={currentFarm} />}
            </div>
            <div className='d-flex align-center farms__line--header__item'>
              <Link to={`/farms/visual-lines/${params.idFarm}`}>
                <Image src={farm_image} style={{ maxWidth: '30px' }} />
              </Link>
            </div>
            {width > 768 ? (
              <div className={'d-flex align-center'}>
                {isEditable && (
                  <DropdownMenu
                    data={currentFarm}
                    column='isFarms'
                    onEdit={handleOnEdit}
                    type='isRedirect'
                    isRedirect='isFarms'
                  />
                )}
              </div>
            ) : (
              <>
                {!currentFarm?.is_flupsy && isEditable && (
                  <Link to={`/farms/${params.idFarm}/add-line`}>
                    <Button
                      color='blue'
                      size={1}
                      width='wide'
                      type='fill'
                      className='farms__line--header__btn'
                    >
                      {translate(lang, 'Add Line')}
                    </Button>
                  </Link>
                )}
              </>
            )}
          </div>
          <div className='d-flex justify-between align-center  pt-10 pb-20'>
            <div className='d-flex align-center' style={{ columnGap: 25 }}>
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className='d-flex bg-main align-center'
                  style={{ padding: '10px 16px' }}
                >
                  <Subtitle
                    size={4}
                    color='black'
                    align='left'
                    fontWeight={500}
                    className='mr-12'
                  >
                    {stat.name === 'Earned' ? (
                      <>
                        <span className='mr-12'>
                          {`${translate(lang, `${stat.name} this year`)}: $ ${stat.this_year}`}
                        </span>
                        <span className='ml-12 mr-12'>
                          {`${translate(lang, `${stat.name} last year`)}: $ ${stat.last_year}`}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className='mr-12'>
                          {`${translate(lang, `${stat.name} this year`)}: ${stat.this_year}`}
                        </span>
                        <span className='ml-12 mr-12'>
                          {`${translate(lang, `${stat.name} last year`)}: ${stat.last_year}`}
                        </span>
                      </>
                    )}
                  </Subtitle>
                  <div className='ml-12'>
                    <InterestGrowth
                      interest={stat.interest.interest}
                      isGrow={stat.interest.isGrow}
                    />
                  </div>
                </div>
              ))}
            </div>
            {isEditable && (
              <div className='d-flex gap-6'>
                <SmallButton type='primary' onClick={() => setVisModify(true)}>
                  {translate(lang, 'Modify columns')}
                </SmallButton>
                <SmallButton
                  type='primary'
                  className='green-btn'
                  onClick={() => setVisBulk(true)}
                >
                  {translate(lang, 'Bulk update')}
                </SmallButton>
              </div>
            )}
          </div>
          {currentFarm && (
            <>
              {width > 768 ? (
                <LinesTable farmData={currentFarm} />
              ) : (
                <LinesMobile farmData={currentFarm} />
              )}
            </>
          )}
        </div>
      </div>
      {!!showBulkHarvest && currentFarm && (
        <BulkHarvestModal
          farm={currentFarm}
          visible={true}
          type={showBulkHarvest}
          onConfirm={() => setShowBulkHarvest(undefined)}
          onCancel={() => setShowBulkHarvest(undefined)}
        />
      )}
      {currentFarm &&
        visModify &&
        (currentFarm.structure === 'RAFT' ? (
          <RaftColumnModifyModal
            visible={true}
            onClose={() => setVisModify(false)}
          />
        ) : (
          <LineColumnModifyModal
            visible={true}
            onClose={() => setVisModify(false)}
          />
        ))}
      {currentFarm && visBulk && (
        <LinesBulkUpdateModal
          visible={true}
          farmData={currentFarm}
          onClose={() => setVisBulk(false)}
        />
      )}
    </div>
  );
};

export default Farm;
